import { useMemo } from 'react';
import { Box, Button, Grid, Stack } from '@mui/material';
import { useSession } from 'App';
import {
  AlertSnackbar,
  AnomaliesSidebar,
  AuditStatuses,
  ConfirmationDialog,
  duration,
  EnhancedTimeline,
  Loader,
  ReviewIdentityDialog,
  Screenshots,
  StatusesMenu,
  useExamSessionReview,
  usePreSignedUrl,
  useScreenshotClocks,
  Video,
} from '@shared';
import { CommentsDialog, ExamInfo } from './components';
import useReviewHook from './Review.hook';
import * as S from './Review.styled';

export function Review() {
  const {
    isPlaying,
    isStatusesLoading,
    isOpenReviewIdentity,
    isOpenComments,
    isConfirmationOpen,
    isActionAlertOpen,

    playedSeconds,
    playbackRate,
    progressPlayed,
    playerRef,

    targetRegions,
    anomalies,
    statuses,
    sessionUuid,
    auditUuid,

    handleSeekChange,
    toggleConfirmationOpen,
    handleApproveConfirm,
    setPlayedSeconds,
    setPlaybackRate,
    setProgressPlayed,
    setIsPlaying,
    setActionAlertOpen,
    setOpenReviewIdentity,
    setOpenComments,
    updateStatuses,
    refetchAnomalies,
  } = useReviewHook();
  const { currentUser } = useSession();
  const { isInstructor, isLeadAuditor } = currentUser.roles;

  const { data, isLoading, isError } = useExamSessionReview(sessionUuid);
  const { data: screenshotClocks = [] } = useScreenshotClocks(data?.streamResponse.streamHandle);

  const { preSignedUrl, refreshPreSignedUrl } = usePreSignedUrl(data?.muxedVideoHandle);

  const confirmText = `Are you sure you want to ${
    statuses?.auditFinished ? 'edit' : 'finish'
  } the audit?`;

  const alertTitle = useMemo(
    // to prevent changing of alert title before it disappear
    () => `Audit has been ${statuses?.auditFinished ? 'edited' : 'finished'}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isConfirmationOpen]
  );
  const streamDuration = data
    ? duration(data.streamResponse.videoStartClock, data.streamResponse.videoEndClock)
    : 0;

  const videoDuration =
    (playerRef.current && playerRef.current.getDuration() * 1000) || streamDuration;

  const roomScanTraceHandle = isLeadAuditor || isInstructor ? data?.roomScanTraceHandle : undefined;

  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return null;
  }

  return (
    <Box component='article' sx={{ pr: '312px', width: '100%', height: '100%' }}>
      <S.TitleWrapper>
        <AuditStatuses statuses={statuses} partnerName={data?.partner.name} auditUuid={auditUuid} />
        <Stack direction='row' gap={1} alignItems='center'>
          <ExamInfo data={data} />
          <Button disabled={isStatusesLoading} variant='contained' onClick={toggleConfirmationOpen}>
            {statuses && statuses.auditFinished ? 'Edit Audit' : 'Finish Audit'}
          </Button>
          <StatusesMenu
            useStatuses={{ statuses, updateStatuses, isStatusesLoading }}
            openReviewIdentity={() => setOpenReviewIdentity(true)}
            openComments={() => setOpenComments(true)}
            auditUuid={auditUuid}
            sessionUuid={sessionUuid}
            isReview
          />
        </Stack>
      </S.TitleWrapper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Video
            src={preSignedUrl}
            playing={isPlaying}
            setPlaying={setIsPlaying}
            onSetProgressPlayed={setProgressPlayed}
            playerRef={playerRef}
            playbackRate={playbackRate}
            onSetPlaybackRate={setPlaybackRate}
            onSetPlayedSeconds={setPlayedSeconds}
            playedSeconds={playedSeconds}
            regions={[]}
            onSeekChange={handleSeekChange}
            videoDuration={videoDuration}
            refreshPreSignedUrl={refreshPreSignedUrl}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Screenshots
            screenshotsClock={screenshotClocks}
            stream={data.streamResponse}
            progressPlayed={progressPlayed}
          />
        </Grid>
      </Grid>
      <EnhancedTimeline
        progressPlayed={progressPlayed}
        videoDuration={videoDuration}
        onSeekChange={handleSeekChange}
        targetRegions={targetRegions}
        anomalies={anomalies}
        isPlaying={isPlaying}
      />
      <AnomaliesSidebar
        auditUuid={auditUuid}
        playedSeconds={playedSeconds}
        onSeekChange={handleSeekChange}
        anomalies={anomalies}
        refetchAnomalies={refetchAnomalies}
        disabled={statuses?.auditFinished}
        isReview={true}
      />
      <ReviewIdentityDialog
        title='Review Identity'
        isModalOpen={isOpenReviewIdentity}
        onCloseModal={() => setOpenReviewIdentity(false)}
        profileTraceHandle={data.profileTraceHandle}
        documentTraceHandle={data.documentTraceHandle}
        roomScanTraceHandle={roomScanTraceHandle}
      />
      <CommentsDialog
        auditUuid={auditUuid}
        isModalOpen={isOpenComments}
        onCloseModal={() => setOpenComments(false)}
      />
      <ConfirmationDialog
        isConfirmationOpen={isConfirmationOpen}
        text={confirmText}
        toggleConfirmationOpen={toggleConfirmationOpen}
        handleApproveConfirm={handleApproveConfirm}
      />
      <AlertSnackbar
        text={alertTitle}
        open={isActionAlertOpen}
        handleClose={() => setActionAlertOpen(false)}
        color='success'
      />
    </Box>
  );
}
